import { Div, IRouteEvent } from '@codesuma/baseline'
import style from './index.module.css'

// A route page that slides in and out. Replaces baseline's Page so the app owns
// the timing: the old one moved both pages 60px in 160ms with no real gap, and
// the two halves blurred into a cross-fade. Here the leaving page clears a full
// 120px first, then a beat of empty screen, then the next page rises in from the
// same distance — three steps you can actually follow.
//
// The router awaits the exit handler before it fires the next page's enter, so
// EXIT_DURATION is what holds the arriving page back until this one is gone;
// ENTER_DELAY is the beat between them.
const EXIT_DURATION = 200
const ENTER_DELAY = 160

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

// `skip` lets a page run some transitions itself: Page stays out of any enter or
// exit it returns true for (Discover grows a circle into the public list page).
export const Page = (skip?: (e: IRouteEvent) => boolean) => {
    const base = Div()
    base.addClass(style.base) // starts hidden below (in CSS)
    base.el.setAttribute('aria-hidden', 'true')

    base.on('enter', async (e: IRouteEvent) => {
        if (skip?.(e)) return
        const { isBack } = e
        // Pre-position on the side we're coming from: forward pages rise from
        // below, a back navigation drops in from above.
        base.addClass(isBack ? style.hiddenAbove : style.hiddenBelow)
        base.removeClass(isBack ? style.hiddenBelow : style.hiddenAbove)

        // Force a reflow so the browser registers the starting position — without
        // this the very first transition jumps straight to the end state.
        void base.el.offsetHeight

        await wait(ENTER_DELAY)

        base.removeClass(style.exitUp, style.exitDown, style.hiddenBelow, style.hiddenAbove)
        base.addClass(isBack ? style.enterDown : style.enterUp)
        base.el.setAttribute('aria-hidden', 'false')
    })

    base.on('exit', async (e: IRouteEvent) => {
        if (skip?.(e)) return
        const { isBack } = e
        base.removeClass(style.enterUp, style.enterDown)
        base.addClass(isBack ? style.exitDown : style.exitUp)

        await wait(EXIT_DURATION)
        base.el.setAttribute('aria-hidden', 'true')

        // Park on the far side, ready for the next enter from the other direction.
        base.addClass(isBack ? style.hiddenBelow : style.hiddenAbove)
    })

    return base
}
